<template>
  <Dialog
    :visible="store.relatedArtifact.visible"
    @update:visible="onVisibleChange"
    modal
    dismissable-mask
    :draggable="false"
    :style="{ width: '720px', maxWidth: '92vw' }"
    :content-style="contentStyle"
    class="related-artifact-dialog"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <i :class="headerIcon" style="color: var(--primary-gold);"></i>
        <span class="font-semibold" style="color: var(--text-primary);">{{ headerLabel }}</span>
      </div>
    </template>

    <!-- Loading (lazy SOP fetch in flight) -->
    <div v-if="loading" class="py-10 text-center">
      <i class="pi pi-spin pi-spinner text-3xl mb-3" style="color: var(--primary-gold);"></i>
      <p style="color: var(--text-secondary);">Loading {{ store.relatedArtifact.id }}...</p>
    </div>

    <!-- Resolved artifact (in-memory section or fetched SOP) -->
    <div v-else-if="hasContent">
      <p v-if="description" class="text-sm mb-3" style="color: var(--text-secondary);">
        {{ description }}
      </p>
      <p
        v-if="readTime"
        class="text-xs mb-4 flex items-center gap-1.5"
        style="color: var(--text-muted);"
      >
        <i class="pi pi-clock"></i>
        <span>{{ readTime }}</span>
      </p>

      <div v-if="renderedBody" class="markdown-content" v-html="renderedBody"></div>
      <p v-else style="color: var(--text-secondary);">
        No overview content is available for this artifact.
      </p>
    </div>

    <!-- Unresolved reference / unavailable -->
    <div v-else class="py-8 text-center">
      <i class="pi pi-exclamation-triangle text-3xl mb-3" style="color: var(--text-muted);"></i>
      <p style="color: var(--text-secondary);">
        Details for <strong>{{ store.relatedArtifact.id }}</strong> are not available.
      </p>
      <p class="text-xs mt-1" style="color: var(--text-muted);">{{ notFoundHint }}</p>
    </div>

    <template #footer>
      <Button label="Close" severity="secondary" text @click="store.closeRelatedArtifact()" />
      <Button
        v-if="showNavButton"
        label="Open full artifact"
        icon="pi pi-arrow-right"
        icon-pos="right"
        @click="openFull"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { useExplorerStore } from '@/stores/explorerStore';
import { useMarkdown } from '@/composables/useMarkdown';

const store = useExplorerStore();
const { renderMarkdown } = useMarkdown();

// Comfortable padding so content isn't flush against the modal edges. Set via
// the prop rather than scoped CSS because PrimeVue's content element does not
// carry this component's scoped-style attribute.
const contentStyle = { padding: '1.25rem 1.5rem 1.5rem' };

const ra = computed(() => store.relatedArtifact);
const isSop = computed(() => ra.value.kind === 'sop');
const section = computed(() => ra.value.section);
const loading = computed(() => ra.value.loading);
const fetched = computed(() => ra.value.fetched);

const hasContent = computed(() => (isSop.value ? !!fetched.value : !!section.value));

// Description / read-time only apply to in-memory domain sections; a fetched SOP
// carries that context inside its own markdown.
const description = computed(() => (isSop.value ? '' : section.value?.description || ''));
const readTime = computed(() => (isSop.value ? '' : section.value?.estimated_read_time || ''));

const headerIcon = computed(() => {
  if (isSop.value) return 'pi pi-book';
  return ra.value.domain === 'process' ? 'pi pi-sitemap' : 'pi pi-shield';
});

const headerLabel = computed(() => {
  if (isSop.value) return fetched.value?.title || ra.value.id;
  const title = section.value?.title;
  if (ra.value.domain === 'process') {
    return title ? `${ra.value.id} — ${title}` : `Process Domain: ${ra.value.id}`;
  }
  return title ? `Policy ${ra.value.id} — ${title}` : `Policy ${ra.value.id}`;
});

const renderedBody = computed(() => {
  if (isSop.value) {
    return fetched.value?.markdown ? renderMarkdown(fetched.value.markdown) : '';
  }
  const s = section.value;
  if (!s) return '';
  // Policy sections carry parsed tab_contents (overview = exec summary +
  // rationale); process sections only carry the full reconstructed markdown.
  const md = s.tab_contents?.overview || s.markdown_content || '';
  return md ? renderMarkdown(md) : '';
});

const domainLabel = computed(() => {
  switch (ra.value.domain) {
    case 'process':
      return 'Process Domain';
    case 'framework':
      return 'Framework';
    default:
      return 'Policy Domain';
  }
});

const notFoundHint = computed(() =>
  isSop.value
    ? 'The knowledge base may be offline, or this SOP is not yet published.'
    : `This reference may point to an artifact that has been renumbered or is not yet in the ${domainLabel.value}.`
);

// Only in-memory domain sections have a sidebar destination to navigate to.
const showNavButton = computed(() => !isSop.value && !!section.value);

const onVisibleChange = (value: boolean) => {
  if (!value) store.closeRelatedArtifact();
};

const openFull = () => {
  if (section.value) {
    store.navigateToArtifact(ra.value.domain, section.value);
  }
};
</script>

<style scoped>
/* Markdown styling for the peek modal (mirrors OverviewTab essentials). */
.markdown-content {
  line-height: 1.7;
  color: var(--text-primary);
  max-height: 60vh;
  overflow-y: auto;
}

.markdown-content :deep(h1) {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1.25rem 0 0.75rem;
  color: var(--text-primary);
}

.markdown-content :deep(h2) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-light);
  padding-bottom: 0.35rem;
}

.markdown-content :deep(h3) {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0.75rem 0 0.4rem;
  color: var(--text-primary);
}

.markdown-content :deep(h4) {
  font-weight: 600;
  margin: 0.6rem 0 0.3rem;
  color: var(--text-primary);
}

.markdown-content :deep(p) {
  margin-bottom: 0.85rem;
  color: var(--text-secondary);
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin-bottom: 0.85rem;
  padding-left: 1.75rem;
}

.markdown-content :deep(li) {
  margin-bottom: 0.4rem;
  color: var(--text-secondary);
}

.markdown-content :deep(a) {
  color: var(--primary-gold);
  text-decoration: underline;
}

.markdown-content :deep(code) {
  background: var(--background-secondary);
  padding: 0.15rem 0.35rem;
  border-radius: 4px;
  font-size: 0.9em;
  font-family: 'Courier New', monospace;
}

.markdown-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0.85rem;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
  border: 1px solid var(--border-light);
  padding: 0.5rem;
  text-align: left;
}

.markdown-content :deep(th) {
  background: var(--background-secondary);
  font-weight: 600;
  color: var(--text-primary);
}

.markdown-content :deep(blockquote) {
  border-left: 3px solid var(--primary-gold);
  padding-left: 0.85rem;
  margin: 0.85rem 0;
  color: var(--text-secondary);
  font-style: italic;
}
</style>
